/**
 * DPDP 2023 compliance: per-category consent registry + right-to-erasure + audit log.
 */

export const DATA_CATEGORIES = [
  'transactions',
  'voice_audio',
  'camera_image',
  'location',
  'embeddings',
  'analytics',
  'crash_logs',
] as const;
export type DataCategory = (typeof DATA_CATEGORIES)[number];

export interface ConsentRecord {
  readonly category: DataCategory;
  readonly granted: boolean;
  readonly grantedAt: string;
  readonly revokedAt: string | null;
  readonly version: string;
}

const STORAGE_KEY = 'empire:dpdp-consent';
const AUDIT_KEY = 'empire:dpdp-audit';
const POLICY_VERSION = '2026.05.14';

type AuditEntry = {
  readonly ts: string;
  readonly event: 'grant' | 'revoke' | 'erasure_request' | 'export';
  readonly category: DataCategory | 'all';
  readonly note?: string;
};

export function getConsentMap(): Record<DataCategory, ConsentRecord | null> {
  const raw = localStorage.getItem(STORAGE_KEY);
  const base: Record<DataCategory, ConsentRecord | null> = {
    transactions: null,
    voice_audio: null,
    camera_image: null,
    location: null,
    embeddings: null,
    analytics: null,
    crash_logs: null,
  };
  if (raw === null) return base;
  try {
    const parsed = JSON.parse(raw) as Partial<Record<DataCategory, ConsentRecord>>;
    return { ...base, ...parsed };
  } catch {
    return base;
  }
}

function persistConsent(map: Record<DataCategory, ConsentRecord | null>): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
}

function appendAudit(e: AuditEntry): void {
  const raw = localStorage.getItem(AUDIT_KEY);
  const list: AuditEntry[] = raw === null ? [] : (JSON.parse(raw) as AuditEntry[]);
  list.push(e);
  localStorage.setItem(AUDIT_KEY, JSON.stringify(list.slice(-500)));
}

export function grant(category: DataCategory): void {
  const map = getConsentMap();
  map[category] = {
    category,
    granted: true,
    grantedAt: new Date().toISOString(),
    revokedAt: null,
    version: POLICY_VERSION,
  };
  persistConsent(map);
  appendAudit({ ts: new Date().toISOString(), event: 'grant', category });
}

export function revoke(category: DataCategory): void {
  const map = getConsentMap();
  const existing = map[category];
  if (existing !== null) {
    map[category] = { ...existing, granted: false, revokedAt: new Date().toISOString() };
  }
  persistConsent(map);
  appendAudit({ ts: new Date().toISOString(), event: 'revoke', category });
}

export function isGranted(category: DataCategory): boolean {
  const map = getConsentMap();
  return map[category]?.granted === true;
}

export async function requestErasure(): Promise<void> {
  appendAudit({ ts: new Date().toISOString(), event: 'erasure_request', category: 'all', note: 'user-initiated' });
  if (typeof navigator !== 'undefined' && 'storage' in navigator && typeof navigator.storage.getDirectory === 'function') {
    try {
      const dir = await navigator.storage.getDirectory();
      for await (const name of (dir as unknown as { keys: () => AsyncIterableIterator<string> }).keys()) {
        try {
          await dir.removeEntry(name, { recursive: true });
        } catch {
          // ignore individual entry failures
        }
      }
    } catch {
      // ignore opfs unavailable
    }
  }
  localStorage.clear();
  sessionStorage.clear();
  if (typeof indexedDB !== 'undefined' && typeof indexedDB.databases === 'function') {
    try {
      const dbs = await indexedDB.databases();
      for (const db of dbs) {
        if (typeof db.name === 'string') indexedDB.deleteDatabase(db.name);
      }
    } catch {
      // ignore
    }
  }
}

export function exportAuditLog(): AuditEntry[] {
  const raw = localStorage.getItem(AUDIT_KEY);
  return raw === null ? [] : (JSON.parse(raw) as AuditEntry[]);
}

export function getPolicyVersion(): string {
  return POLICY_VERSION;
}
